// =============================================================================
// LISTING AXIS READS — the per-POSTING axis-lens read store (AXIS-P1, plan
// §2, THE SYNTHESIS v2 phasing table P1). THE OWNER'S FRAMING (mission brief,
// verbatim): "the read is a property of the POSTING, not the viewer" —
// computed ONCE per listing, shared by every user who ever looks at that
// posting. Personal offsets (a per-USER retune) are P5's job, not this
// table's — this table carries zero user_id by design (mirrors role_axes'
// own shared-lake posture).
//
// KEYED BY canonicalUrl, not job.id — job.id is a per-user/per-row identity
// (apps/api/lib/jobReads.ts's own header: "job_scores/cover_letters/resumes —
// all keyed by the URL-param job id... never redirect a caller's notion of
// which row is this"), so two different users' OWN `jobs` rows for the SAME
// real-world posting carry two DIFFERENT ids today (legacy per-user jobs
// table has no cross-user identity concept — jobs_lake.ts's own header: "jobs
// / job_extractions / job_scores are completely untouched," so a legacy job
// has no job_listings.id to key off). canonicalUrl (unique on BOTH jobs.ts
// and job_listings.ts) IS the one identity that already means "the same
// real-world posting" in both worlds — reading it as the axis-read's key
// costs nothing additively and correctly shares a lake-listing's read across
// every user while ALSO caching a legacy per-user job's read across repeat
// views by that same user (never wrong, never double-computed for the common
// "one user revisits their own tailored job" case; the cross-user sharing
// benefit is realized in full once a posting has been promoted to
// job_listings, which most postings above the derivation coverage floor
// already are per M3's promotion path).
//
// ONE CLAUDE CALL PER LISTING, EVER: AxisLensService's serve path checks this
// table FIRST; only derives + inserts when absent. A second (or Nth) user (or
// the SAME user's repeat view) reads the stored row — zero re-derivation,
// zero re-cost, per THE SYNTHESIS v2 §7 cost posture.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool
// (docs/design/axis-lens-p1-neon.sql — NOT applied by this lane), mirrored
// here in Drizzle and exported from index.ts.
// =============================================================================

import { sql } from 'drizzle-orm';
import { index, integer, jsonb, pgTable, real, text, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** One axis's weight in a single listing's read. `axisKey` matches a
 *  role_axes.axis_key value (soft-referenced, no FK — mirrors this
 *  codebase's house convention for a lake-row pointer, e.g.
 *  achievement_alignment_rulings.achievementId's own soft-FK reasoning: axes
 *  are edited/merged over time, and a hard FK would fight that). `weight` is
 *  0..1 (this listing's share of read-attention on this axis, per the plan's
 *  "this role reads 40% leadership" framing — the full weights[] array need
 *  not sum to 1 if coverage is partial). `matchedCues` is the traceability
 *  record NO INVENTION requires: the literal cue phrase(s) from
 *  role_axes.detection_cues this weight was actually traced to in the JD's
 *  own text — a weight with an empty matchedCues never should have been kept
 *  (see AxisLensService's post-pass gate). */
export interface ListingAxisWeight {
  axisKey: string;
  weight: number;
  matchedCues: string[];
}

export const listingAxisReads = pgTable(
  'listing_axis_reads',
  {
    id: id(),
    /** The posting's stable cross-user identity — see file header. UNIQUE:
     *  one read per listing, ever (until a taxonomy_version bump makes a
     *  fresh read meaningful — P1 does not re-derive on version bump; that
     *  invariant is explicitly a P4 concern per red-team S3, not decided
     *  here). */
    canonicalUrl: text('canonical_url').notNull().unique(),
    /** The weight vector — ListingAxisWeight[]. Only axes the post-pass could
     *  actually TRACE to real JD text are ever present (never a padded-out
     *  full axis list with invented zeros). */
    weights: jsonb('weights').$type<ListingAxisWeight[]>().notNull().default(sql`'[]'::jsonb`),
    /** 'intern' | 'early' | 'mid' | 'senior' | 'exec' — the seniority READING
     *  of this posting (plan §2: "seniority is a register, not an axis").
     *  Distinct type/name from deriveRegister.ts's `Register`
     *  ('conversational'|'neutral'|'formal', a cover-letter TONE concept) —
     *  never conflated; see AxisLensService.ts's own header for the
     *  reconciliation note. */
    register: varchar('register', { length: 16 }).notNull(),
    /** 0..1 — how much of the JD's OWN requirement text got claimed by axis
     *  cues (plan §2/§3). Deterministic post-pass measurement, never the
     *  model's own self-report (mission brief: "a deterministic post-pass
     *  over the model's claimed mappings, not model self-report"). */
    coverage: real('coverage').notNull(),
    /** Set only when coverage reads as genuinely low (or the JD text itself
     *  was too thin to read confidently, red-team S5) — the honest served
     *  note ('reads_unusual' | 'jd_too_thin' | 'unstructured_read'), never a
     *  forced-fit weight vector. Null on a normal, well-covered structured
     *  read. 'unstructured_read' is the JD-source seam's note (H2 fix,
     *  2026-07-10): the read was measured over the posting's DESCRIPTION
     *  because no requirement bullets were ever extracted — an honest,
     *  fully-traced read, provenance named so the FE can caption it. */
    coverageNote: varchar('coverage_note', { length: 32 }),
    /** WHICH text source the coverage + thinness gate actually read for this
     *  row — the JD-source seam (H2 fix, 2026-07-10; docs/diagrams/
     *  c4-axisread-source-seam-2026-07-10.html). 'requirements' = a structured
     *  ask existed and was the sole denominator (byte-identical to the
     *  pre-seam requirements-only path, so a padded description can never
     *  disguise a thin structured ask — red-team S5/A2 stays closed);
     *  'description' = no bullets were ever captured (a lake-ingest artifact,
     *  not a thin ask), so the description body was the honest denominator
     *  (served with coverage_note 'unstructured_read'); 'absent' = nothing
     *  readable at all. NULL on a pre-seam legacy row — provenance was never
     *  recorded, and is never back-guessed (NO INVENTION). Additive column
     *  (docs/design/axisread-source-kind-neon.sql). */
    sourceKind: varchar('source_kind', { length: 16 }),
    /** Which role_axes taxonomy pass this read was computed against — the
     *  re-derive key for whenever P4's growth loop needs to decide whether a
     *  stored read is stale relative to the CURRENT axis set (red-team S3;
     *  P1 does not implement re-derivation, only stamps the version so P4
     *  can). */
    taxonomyVersion: integer('taxonomy_version').notNull().default(1),
    ...timestamps,
  },
  (t) => ({
    canonicalUrlIdx: index('listing_axis_reads_canonical_url_idx').on(t.canonicalUrl),
  }),
);
